/**
 * lazyWithReload — drop-in replacement for React.lazy that auto-recovers
 * from stale-chunk errors after a deploy.
 *
 * Problem this solves: when a long-lived browser tab survives one or more
 * Vercel deploys, the loaded `index.html` references chunks (e.g.
 * `StudioCanvas-C1gq1rze.js`) that have been superseded on the CDN. Any
 * `lazy(() => import(...))` triggered from that stale tab 404s with
 * "Failed to fetch dynamically imported module," and the React error
 * boundary catches it. The user sees a broken canvas / modal / page until
 * they manually hard-refresh.
 *
 * This helper wraps the import factory: if it fails AND the failure looks
 * like a stale-chunk fetch (not a real bug), trigger ONE hard reload per
 * session. The session flag prevents reload loops if the chunk genuinely
 * doesn't exist on the new deploy. After a successful import, the flag is
 * cleared so subsequent stale-chunk events can also trigger one recovery.
 */

import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

const RELOAD_FLAG = 'skettraStaleChunkReload';

function isStaleChunkError(err: unknown): boolean {
  const msg = String((err as { message?: string })?.message ?? err ?? '');
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('Loading chunk')
  );
}

export function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() =>
    factory()
      .then((mod) => {
        // First successful load after a recovery — clear the flag so the next
        // stale-chunk event in the same session can also auto-recover.
        try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* private mode etc. */ }
        return mod;
      })
      .catch((err) => {
        const stale = isStaleChunkError(err);
        let alreadyReloaded = false;
        try { alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG) === '1'; } catch { /* ignore */ }
        if (stale && !alreadyReloaded) {
          try { sessionStorage.setItem(RELOAD_FLAG, '1'); } catch { /* ignore */ }
          // Force a fresh load of index.html so the latest chunk hashes resolve.
          window.location.reload();
          // Return a never-resolving promise so React's Suspense fallback
          // stays put while the reload happens.
          return new Promise<{ default: T }>(() => {});
        }
        // Either not a stale-chunk error or we already tried once — clear the
        // flag so a future genuine stale-chunk event (a later deploy in the
        // same session) can still recover, then re-throw.
        try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* ignore */ }
        throw err;
      }),
  );
}
